import { useAuth0 } from '@auth0/auth0-react';
import { useParams } from 'react-router';
import useSWR, { SWRResponse } from 'swr';
import http from '@/Utils/HTTP/NewHttpUtils';

export type SearchProgressStatus = 'NotStarted' | 'InProgress' | 'Completed' | 'Failed';
export type RankingStatus = 'InProgress' | 'Successful' | 'Failed';
export type BackgroundJobStatus = 'Queued' | 'InProgress' | 'Reattempting' | 'Successful' | 'Failed';

interface RankingJobResponse {
    id: string;
    jobId: string | null;
    status: BackgroundJobStatus | null;
}

export interface SearchProgressResponse {
    isInProgress: boolean;
    searchStatus: SearchProgressStatus | null;
    rankingStatus: RankingStatus | null;
    rankingJobs: Array<RankingJobResponse> | null;
}

const DefaultSearchProgressResponse: SearchProgressResponse = {
    isInProgress: true,
    searchStatus: null,
    rankingStatus: null,
    rankingJobs: [],
};

const useAISearchProgress = (): SWRResponse<SearchProgressResponse> => {
    const { assessmentId } = useParams();
    const { getAccessTokenSilently } = useAuth0();

    return useSWR(
        `recruitments/${assessmentId}/ai-search/search-progress`,
        async (path) => {
            const response = await http.get<SearchProgressResponse>({
                path,
                accessToken: await getAccessTokenSilently(),
            });

            return response || DefaultSearchProgressResponse;
        },
        {
            // Use SWR's conditional polling - poll every 1 second when isInProgress is true
            refreshInterval: (data: SearchProgressResponse | undefined) => {
                // Stop polling when isInProgress is false
                return data?.isInProgress ? 1000 : 0;
            },
        },
    );
};

export default useAISearchProgress;
